import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Eye,
  GripVertical,
  Pencil,
  Plus,
  Save,
  Send,
  ServerCog,
  X,
} from 'lucide-react'
import {
  allFields,
  FIELD_TYPES,
  slugifyKey,
  validateFormSchema,
  type AnswerValidation,
  type Answers,
  type FieldType,
  type FormField,
  type FormSchema,
  type FormSection,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  FormRenderer,
  Input,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { ApiRequestError } from '@concours/api-client'
import { errorMessage } from '../../lib/errors'
import { FORMS_KEY, type VersionWithSchema } from '../../lib/form-types'
import { ConditionEditor } from './ConditionEditor'
import { FieldProperties } from './FieldProperties'

type Selection = { section: number; field?: string } | null

/** Valeurs par défaut d'un champ ajouté depuis la palette. */
function newField(type: FieldType, schema: FormSchema, before: FormField[]): FormField {
  const label =
    type === 'checkbox'
      ? 'Je certifie sur l’honneur l’exactitude des informations'
      : FIELD_TYPES[type].label
  const base = slugifyKey(type === 'checkbox' ? 'declaration' : label)
  const taken = new Set(allFields(schema).map((f) => f.key))
  let key = base
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`.slice(0, 50)
  const field: FormField = { key, type, label }
  if (['select', 'radio', 'multiselect'].includes(type))
    field.options = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
    ]
  if (type === 'file') field.file = { formats: ['pdf', 'jpg'], maxSizeMb: 5 }
  if (type === 'image') field.file = { formats: ['jpg', 'png'], maxSizeMb: 2 }
  if (type === 'computed') {
    const date = before.find((f) => f.type === 'date')
    field.label = 'Âge'
    field.key = taken.has('age') ? key : 'age'
    field.compute = {
      fn: 'age_at',
      field: date?.key ?? '',
      at: `${new Date().getFullYear()}-12-31`,
    }
    field.unit = 'ans'
  }
  if (type === 'readonly') {
    field.label = 'Matricule de candidature'
    field.key = taken.has('matricule') ? key : 'matricule'
    field.source = 'application_number'
  }
  if (type === 'checkbox') field.required = true
  return field
}

const PALETTE = Object.entries(FIELD_TYPES).reduce<Record<string, [FieldType, string][]>>(
  (groups, [type, def]) => {
    ;(groups[def.group] ??= []).push([type as FieldType, def.label])
    return groups
  },
  {}
)

/**
 * Constructeur de formulaire (TODO phase 7) : palette, sections et champs
 * réordonnables (souris ou clavier), panneau de propriétés, erreurs en direct
 * (même validateur que l'API), aperçu identique au portail, test côté serveur.
 */
export function FormBuilder({
  formId,
  draft,
  publishedKeys,
  canEdit,
}: {
  formId: string
  draft: VersionWithSchema
  /** Clés de la dernière version publiée (figées). */
  publishedKeys: Set<string>
  canEdit: boolean
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [schema, setSchema] = useState<FormSchema>(draft.schema)
  const [saved, setSaved] = useState(JSON.stringify(draft.schema))
  const [selection, setSelection] = useState<Selection>({ section: 0 })
  const [preview, setPreview] = useState(false)
  const [answers, setAnswers] = useState<Answers>({})
  const [serverCheck, setServerCheck] = useState<AnswerValidation | null>(null)
  const [publishing, setPublishing] = useState(false)
  const dirty = JSON.stringify(schema) !== saved

  const { issues } = useMemo(() => validateFormSchema(schema), [schema])
  const fields = allFields(schema)

  const save = useMutation({
    mutationFn: () =>
      session.api.put<VersionWithSchema>(`/admin/forms/${formId}/draft`, { schema }),
    onSuccess: async (version) => {
      setSaved(JSON.stringify(version.schema))
      toast.success('Brouillon enregistré')
      await queryClient.invalidateQueries({ queryKey: [...FORMS_KEY, formId] })
    },
    onError: (error) => toast.error(errorMessage(error), serverIssues(error)),
  })
  const publish = useMutation({
    mutationFn: async (reason?: string) => {
      if (dirty) await save.mutateAsync()
      return session.api.post<{ version: number }>(`/admin/forms/${formId}/publish`, {
        reason: reason || null,
      })
    },
    onSuccess: async (version) => {
      setPublishing(false)
      toast.success(`Version ${version.version} publiée`, 'Elle est désormais immuable.')
      await queryClient.invalidateQueries({ queryKey: FORMS_KEY })
    },
    onError: (error) => {
      setPublishing(false)
      toast.error(errorMessage(error), serverIssues(error))
    },
  })
  const test = useMutation({
    mutationFn: async () => {
      if (dirty) await save.mutateAsync()
      return session.api.post<AnswerValidation>(
        `/admin/forms/${formId}/versions/${draft.id}/test`,
        { answers }
      )
    },
    onSuccess: setServerCheck,
    onError: (error) => toast.error(errorMessage(error)),
  })

  /* ---------------- Modifications ---------------- */

  const updateSection = (index: number, patch: Partial<FormSection>) =>
    setSchema((s) => ({
      sections: s.sections.map((sec, i) => (i === index ? { ...sec, ...patch } : sec)),
    }))

  const updateField = (sectionIndex: number, key: string, next: FormField) => {
    setSchema((s) => ({
      sections: s.sections.map((sec, i) =>
        i === sectionIndex
          ? { ...sec, fields: sec.fields.map((f) => (f.key === key ? next : f)) }
          : sec
      ),
    }))
    if (next.key !== key) setSelection({ section: sectionIndex, field: next.key })
  }

  const addField = (type: FieldType) => {
    const index = selection?.section ?? schema.sections.length - 1
    const section = schema.sections[index]
    if (!section) return
    const before = schema.sections.slice(0, index + 1).flatMap((s) => s.fields)
    const field = newField(type, schema, before)
    updateSection(index, { fields: [...section.fields, field] })
    setSelection({ section: index, field: field.key })
  }

  const removeField = (sectionIndex: number, key: string) => {
    const section = schema.sections[sectionIndex]!
    updateSection(sectionIndex, { fields: section.fields.filter((f) => f.key !== key) })
    setSelection({ section: sectionIndex })
  }

  const moveField = (from: number, key: string, toKey: string) => {
    const to = schema.sections.findIndex((s) => s.key === toKey)
    if (to < 0 || to === from) return
    const field = schema.sections[from]!.fields.find((f) => f.key === key)!
    setSchema((s) => ({
      sections: s.sections.map((sec, i) =>
        i === from
          ? { ...sec, fields: sec.fields.filter((f) => f.key !== key) }
          : i === to
            ? { ...sec, fields: [...sec.fields, field] }
            : sec
      ),
    }))
    setSelection({ section: to, field: key })
  }

  const addSection = () => {
    const taken = new Set(schema.sections.map((s) => s.key))
    let n = schema.sections.length + 1
    while (taken.has(`section_${n}`)) n++
    setSchema((s) => ({
      sections: [...s.sections, { key: `section_${n}`, title: `Section ${n}`, fields: [] }],
    }))
    setSelection({ section: schema.sections.length })
  }

  const moveSection = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= schema.sections.length) return
    setSchema((s) => ({ sections: arrayMove(s.sections, index, target) }))
    setSelection({ section: target })
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const onDragEnd = (sectionIndex: number) => (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const section = schema.sections[sectionIndex]!
    const from = section.fields.findIndex((f) => f.key === active.id)
    const to = section.fields.findIndex((f) => f.key === over.id)
    updateSection(sectionIndex, { fields: arrayMove(section.fields, from, to) })
  }

  /* ---------------- Rendu ---------------- */

  const selectedSection = selection ? schema.sections[selection.section] : undefined
  const selectedField = selection?.field
    ? selectedSection?.fields.find((f) => f.key === selection.field)
    : undefined
  /** Champs situés AVANT une section (key absente) ou avant un champ de cette section. */
  const fieldsBefore = (sectionIndex: number, key?: string) => {
    const previous = schema.sections.slice(0, sectionIndex).flatMap((s) => s.fields)
    if (key === undefined) return previous
    const own = schema.sections[sectionIndex]!.fields
    return [
      ...previous,
      ...own.slice(
        0,
        own.findIndex((f) => f.key === key)
      ),
    ]
  }
  const issueFor = (path: string) => issues.some((i) => i.path.startsWith(path))
  const fieldPath = (sectionIndex: number, key: string) =>
    `sections.${sectionIndex}.fields.${schema.sections[sectionIndex]!.fields.findIndex((f) => f.key === key)}`

  return (
    <div className="stack" style={{ gap: 'var(--space-4)' }}>
      <Card className="perm-savebar">
        <CardBody>
          <div
            className="row"
            style={{ gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Badge variant="neutral">Brouillon</Badge>
            <span className="text-muted" aria-live="polite" style={{ marginInlineEnd: 'auto' }}>
              {fields.length} champ{fields.length > 1 ? 's' : ''} · {schema.sections.length} section
              {schema.sections.length > 1 ? 's' : ''} ·{' '}
              {dirty ? 'modifications non enregistrées' : 'enregistré'}
              {issues.length > 0 && ` · ${issues.length} problème${issues.length > 1 ? 's' : ''}`}
            </span>
            <Button
              variant="secondary"
              icon={preview ? <Pencil /> : <Eye />}
              onClick={() => setPreview((p) => !p)}
            >
              {preview ? 'Retour à l’édition' : 'Aperçu et test'}
            </Button>
            {canEdit && (
              <Button
                variant="secondary"
                icon={<Save />}
                loading={save.isPending}
                disabled={!dirty || issues.length > 0}
                onClick={() => save.mutate()}
              >
                Enregistrer le brouillon
              </Button>
            )}
            <Can perm="forms.publish">
              <Button
                variant="primary"
                icon={<Send />}
                disabled={issues.length > 0}
                onClick={() => setPublishing(true)}
              >
                Publier
              </Button>
            </Can>
          </div>
        </CardBody>
      </Card>

      {issues.length > 0 && !preview && (
        <Alert tone="warning" title="À corriger avant d’enregistrer">
          <ul style={{ margin: 0, paddingInlineStart: 'var(--space-5)' }}>
            {issues.slice(0, 8).map((issue) => (
              <li key={issue.path + issue.message}>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    const m = /^sections\.(\d+)(?:\.fields\.(\d+))?/.exec(issue.path)
                    if (!m) return
                    const s = Number(m[1])
                    const f =
                      m[2] === undefined ? undefined : schema.sections[s]?.fields[Number(m[2])]?.key
                    setSelection({ section: s, field: f })
                  }}
                >
                  {issue.message}
                </button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {preview ? (
        <div className="builder-preview">
          <Card>
            <CardHeader title="Aperçu (rendu identique au portail candidat)" />
            <CardBody>
              {issues.length ? (
                <Alert tone="warning">Corrigez les problèmes du formulaire pour l’aperçu.</Alert>
              ) : (
                <FormRenderer
                  schema={schema}
                  value={answers}
                  onChange={(next) => {
                    setAnswers(next)
                    setServerCheck(null)
                  }}
                  readonlyValues={{
                    application_number: 'PEN-2026-A7K9Q2',
                    user_number: 'CAN-7K9Q2A4M',
                    competition_reference: 'EXEMPLE-2026',
                    email: 'candidat@exemple.sn',
                  }}
                  serverErrors={serverCheck?.errors}
                  onComplete={() =>
                    toast.success('Formulaire complet', 'Les contrôles du portail sont satisfaits.')
                  }
                />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Test côté serveur" />
            <CardBody>
              <p className="text-muted" style={{ marginBlockStart: 0 }}>
                Envoie les réponses saisies à l’API, qui applique exactement la validation du dépôt.
              </p>
              <Button
                variant="secondary"
                icon={<ServerCog />}
                loading={test.isPending}
                disabled={issues.length > 0}
                onClick={() => test.mutate()}
              >
                Tester ces réponses
              </Button>
              {serverCheck && (
                <div
                  className="stack"
                  style={{ marginBlockStart: 'var(--space-4)', gap: 'var(--space-2)' }}
                >
                  {Object.keys(serverCheck.errors).length ? (
                    <Alert
                      tone="danger"
                      title={`${Object.keys(serverCheck.errors).length} erreur(s) côté serveur`}
                    >
                      <ul style={{ margin: 0, paddingInlineStart: 'var(--space-5)' }}>
                        {Object.entries(serverCheck.errors).map(([k, m]) => (
                          <li key={k}>
                            <span className="mono">{k}</span> : {m}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  ) : (
                    <Alert tone="success">Accepté par le serveur.</Alert>
                  )}
                  <pre className="audit-json">{JSON.stringify(serverCheck.values, null, 2)}</pre>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      ) : (
        <div className="builder">
          {/* Palette */}
          <aside className="builder-palette" aria-label="Types de champs">
            {canEdit &&
              Object.entries(PALETTE).map(([group, types]) => (
                <div key={group}>
                  <p className="nav-section-title">{group}</p>
                  <div className="stack" style={{ gap: 4 }}>
                    {types.map(([type, label]) => (
                      <button
                        key={type}
                        type="button"
                        className="palette-item"
                        onClick={() => addField(type)}
                      >
                        <Plus aria-hidden="true" size={14} /> {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            {canEdit && (
              <Button variant="secondary" size="sm" icon={<Plus />} onClick={addSection}>
                Nouvelle section
              </Button>
            )}
          </aside>

          {/* Canevas */}
          <div className="builder-canvas">
            {schema.sections.map((section, si) => (
              <section
                key={section.key}
                className="builder-section"
                data-selected={selection?.section === si && !selection.field ? true : undefined}
                aria-label={section.title}
              >
                <div className="builder-section-hd">
                  <button
                    type="button"
                    className="builder-section-title"
                    onClick={() => setSelection({ section: si })}
                  >
                    {section.title}
                    {section.visibility && <Badge variant="sahel">Conditionnelle</Badge>}
                    {issueFor(`sections.${si}.`) || issueFor(`sections.${si}.key`) ? (
                      <AlertTriangle aria-label="Problème" size={14} className="text-danger" />
                    ) : null}
                  </button>
                  {canEdit && (
                    <span className="row" style={{ gap: 2 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<ArrowUp />}
                        aria-label={`Monter la section ${section.title}`}
                        disabled={si === 0}
                        onClick={() => moveSection(si, -1)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<ArrowDown />}
                        aria-label={`Descendre la section ${section.title}`}
                        disabled={si === schema.sections.length - 1}
                        onClick={() => moveSection(si, 1)}
                      />
                    </span>
                  )}
                </div>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd(si)}
                >
                  <SortableContext
                    items={section.fields.map((f) => f.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    <ul className="builder-fields">
                      {section.fields.map((field) => (
                        <SortableField
                          key={field.key}
                          field={field}
                          disabled={!canEdit}
                          selected={selection?.section === si && selection.field === field.key}
                          problem={issueFor(fieldPath(si, field.key))}
                          onSelect={() => setSelection({ section: si, field: field.key })}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
                {!section.fields.length && (
                  <p className="text-muted builder-empty">
                    Section vide : ajoutez des champs depuis la palette.
                  </p>
                )}
              </section>
            ))}
          </div>

          {/* Propriétés */}
          <aside className="builder-props" aria-label="Propriétés">
            {selectedField && selection ? (
              <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
                <FieldProperties
                  key={`${selection.section}-${selectedSection!.fields.indexOf(selectedField)}`}
                  field={selectedField}
                  onChange={(next) => updateField(selection.section, selectedField.key, next)}
                  before={fieldsBefore(selection.section, selectedField.key)}
                  keyLocked={publishedKeys.has(selectedField.key)}
                  sections={schema.sections.map((s) => ({ key: s.key, title: s.title }))}
                  sectionKey={selectedSection!.key}
                  onMoveSection={(to) => moveField(selection.section, selectedField.key, to)}
                  onRemove={() => removeField(selection.section, selectedField.key)}
                />
              </fieldset>
            ) : selectedSection && selection ? (
              <fieldset
                disabled={!canEdit}
                className="stack"
                style={{ border: 0, padding: 0, margin: 0, gap: 'var(--space-4)' }}
              >
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>Section</strong>
                  {schema.sections.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<X />}
                      onClick={() => {
                        setSchema((s) => ({
                          sections: s.sections.filter((_, i) => i !== selection.section),
                        }))
                        setSelection({ section: 0 })
                      }}
                    >
                      Supprimer la section
                    </Button>
                  )}
                </div>
                <Input
                  label="Titre"
                  required
                  value={selectedSection.title}
                  onChange={(e) => updateSection(selection.section, { title: e.target.value })}
                />
                <Input
                  label="Clé technique"
                  mono
                  value={selectedSection.key}
                  onChange={(e) =>
                    updateSection(selection.section, {
                      key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                    })
                  }
                />
                <Textarea
                  label="Description"
                  rows={2}
                  value={selectedSection.description ?? ''}
                  onChange={(e) =>
                    updateSection(selection.section, { description: e.target.value || undefined })
                  }
                />
                <fieldset className="prop-group">
                  <legend>Section conditionnelle</legend>
                  <ConditionEditor
                    value={selectedSection.visibility}
                    onChange={(visibility) => updateSection(selection.section, { visibility })}
                    fields={fieldsBefore(selection.section)}
                    subject="de la section"
                  />
                </fieldset>
              </fieldset>
            ) : (
              <p className="text-muted">Sélectionnez une section ou un champ.</p>
            )}
          </aside>
        </div>
      )}

      <ConfirmModal
        open={publishing}
        onClose={() => setPublishing(false)}
        title="Publier cette version ?"
        message={
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <p style={{ margin: 0 }}>
              La version publiée devient <strong>immuable</strong>. Les concours déjà associés
              gardent leur version ; associez la nouvelle depuis la fiche du concours (procédure
              contrôlée si les dépôts sont ouverts).
            </p>
          </div>
        }
        confirmLabel="Publier"
        reasonRequired
        reasonLabel="Objet de la version (motif)"
        onConfirm={async (reason) => {
          await publish.mutateAsync(reason).catch(() => undefined)
        }}
      />
    </div>
  )
}

function serverIssues(error: unknown) {
  if (error instanceof ApiRequestError && error.code === 'E_FORM_INVALID')
    return (error.details as { issues: { message: string }[] }).issues
      .map((i) => i.message)
      .join(' · ')
  return undefined
}

function SortableField({
  field,
  selected,
  problem,
  disabled,
  onSelect,
}: {
  field: FormField
  selected: boolean
  problem: boolean
  disabled: boolean
  onSelect: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.key,
    disabled,
  })
  return (
    <li
      ref={setNodeRef}
      className="builder-field"
      data-selected={selected || undefined}
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {!disabled && (
        <button
          type="button"
          className="drag-handle"
          aria-label={`Déplacer « ${field.label} »`}
          {...attributes}
          {...listeners}
        >
          <GripVertical aria-hidden="true" size={16} />
        </button>
      )}
      <button
        type="button"
        className="builder-field-main"
        onClick={onSelect}
        aria-pressed={selected}
      >
        <span className="builder-field-label">
          {field.label || <em>Sans libellé</em>}
          {field.required && (
            <span className="req" aria-label=" (obligatoire)">
              *
            </span>
          )}
        </span>
        <span className="builder-field-meta">
          {' '}
          {FIELD_TYPES[field.type]?.label ?? field.type} · <span className="mono">{field.key}</span>
          {field.visibility && <Badge variant="sahel">Conditionnel</Badge>}
          {problem && <AlertTriangle aria-label="Problème" size={14} className="text-danger" />}
        </span>
      </button>
    </li>
  )
}
